/**
 * EmptyState — the canonical "nothing here yet" treatment.
 *
 * Unifies the ad-hoc empty states scattered across the feature pages
 * (some were a bare `<p>`, some a centred card, some a left-aligned
 * icon+text row). Always: an icon + a clear title + an optional hint of
 * what populates it.
 *
 * Two layouts:
 *   variant="card"   — centred card (default; good for whole-page empties)
 *   variant="inline" — left-aligned icon + text row inside an existing card
 */
import { clsx } from 'clsx'
import { Inbox } from 'lucide-react'

export default function EmptyState({
  icon: Icon = Inbox,
  title,
  hint,
  tone = 'text-text-muted',
  variant = 'card',
  className,
  children,
}: {
  icon?: React.ElementType
  title: React.ReactNode
  hint?: React.ReactNode
  /** Icon colour util (e.g. 'text-emerald-light' for a reassuring empty). */
  tone?: string
  variant?: 'card' | 'inline'
  className?: string
  children?: React.ReactNode
}) {
  if (variant === 'inline') {
    return (
      <div className={clsx('card p-4 text-text-muted text-sm flex items-start gap-3', className)}>
        <Icon size={16} className={clsx('flex-shrink-0 mt-0.5', tone)} />
        <div>
          <div className="text-text-primary font-medium mb-1">{title}</div>
          {hint && <div className="text-[12px]">{hint}</div>}
          {children}
        </div>
      </div>
    )
  }
  return (
    <div className={clsx('card p-8 text-center', className)}>
      <Icon size={28} className={clsx('mx-auto mb-2', tone)} />
      <div className="text-text-primary font-medium">{title}</div>
      {hint && <div className="text-[12px] text-text-muted mt-1">{hint}</div>}
      {children}
    </div>
  )
}
